"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.widgetDatasheetSelector = exports.aggregationWidgetCollaborators = exports.aggregationWidgetPermission = exports.initRootWidgetState = exports.getWidgetErrorCode = void 0;
const core_1 = require("../core");
const reducer_1 = require("./slice/permission/reducer");
const getWidgetErrorCode = (state, widgetId) => {
    const widgetSnapshot = core_1.Selectors.getWidgetSnapshot(state, widgetId);
    if (!widgetSnapshot) {
        return;
    }
    const datasheetId = widgetSnapshot.datasheetId;
    const sourceId = widgetSnapshot.sourceId;
    return (sourceId === null || sourceId === void 0 ? void 0 : sourceId.startsWith('mir')) ? core_1.Selectors.getMirrorErrorCode(state, sourceId) : core_1.Selectors.getDatasheetErrorCode(state, datasheetId);
};
exports.getWidgetErrorCode = getWidgetErrorCode;
/**
  * init widgetState;
  * @param state
  * @param widgetId
  */
const initRootWidgetState = (state, widgetId, opt) => {
    var _a;
    const widget = core_1.Selectors.getWidget(state, widgetId);
    const datasheetId = widget.snapshot.datasheetId;
    const mirrorId = (_a = widget.snapshot) === null || _a === void 0 ? void 0 : _a.sourceId;
    const bindDatasheet = (0, exports.widgetDatasheetSelector)(state, datasheetId);
    const mirrorPack = mirrorId ? core_1.Selectors.getMirrorPack(state, mirrorId) : undefined;
    const datasheetMap = bindDatasheet ? { [datasheetId]: bindDatasheet } : {};
    const foreignDatasheetIds = (opt === null || opt === void 0 ? void 0 : opt.foreignDatasheetIds) || [];
    foreignDatasheetIds.forEach(dstId => {
        if (dstId === datasheetId) {
            return;
        }
        const datasheet = (0, exports.widgetDatasheetSelector)(state, dstId, true);
        if (datasheet) {
            datasheetMap[dstId] = datasheet;
        }
    });
    return {
        widget,
        datasheetMap,
        unitInfo: state.unitInfo,
        pageParams: state.pageParams,
        mirrorMap: mirrorPack ? { [mirrorId]: mirrorPack } : undefined,
        user: state.user.info,
        errorCode: (0, exports.getWidgetErrorCode)(state, widgetId) || null,
        permission: (0, exports.aggregationWidgetPermission)(state, widgetId),
        collaborators: (0, exports.aggregationWidgetCollaborators)(state)
    };
};
exports.initRootWidgetState = initRootWidgetState;
/**
  * Aggregate widget permissions.
  * @param state
  * @param widgetId
  */
const aggregationWidgetPermission = (state, widgetId) => {
    var _a;
    const widget = core_1.Selectors.getWidget(state, widgetId);
    if (!widget) {
        return reducer_1.DEFAULT_WIDGET_PERMISSION;
    }
    const datasheetId = widget.snapshot.datasheetId;
    const sourceId = widget.snapshot.datasheetId;
    const mirrorId = (sourceId === null || sourceId === void 0 ? void 0 : sourceId.startsWith('mir')) ? (_a = widget.snapshot) === null || _a === void 0 ? void 0 : _a.sourceId : undefined;
    const datasheetPermission = core_1.Selectors.getPermissions(state, datasheetId, undefined, mirrorId);
    // If in the dashboard, node permissions are required.
    const onDashboard = Boolean(state.pageParams.dashboardId);
    return {
        storage: {
            editable: onDashboard ? core_1.Selectors.getDashboardPermission(state).editable : datasheetPermission.editable
        },
        datasheet: datasheetPermission
    };
};
exports.aggregationWidgetPermission = aggregationWidgetPermission;
/**
  * Aggregate widget collaborators.
  * Change according to the node you are currently at.
  * @param state
  */
const aggregationWidgetCollaborators = (state) => {
    const { dashboardId, datasheetId, mirrorId } = state.pageParams;
    const resourceId = dashboardId || mirrorId || datasheetId;
    let resourceType;
    if (dashboardId) {
        resourceType = core_1.ResourceType.Dashboard;
    }
    else if (mirrorId) {
        resourceType = core_1.ResourceType.Mirror;
    }
    else if (datasheetId) {
        resourceType = core_1.ResourceType.Datasheet;
    }
    else {
        return [];
    }
    return core_1.Selectors.getResourceCollaborator(state, resourceId, resourceType) || [];
};
exports.aggregationWidgetCollaborators = aggregationWidgetCollaborators;
/**
  * Get the datasheet state required by the widget in one go.
  */
const widgetDatasheetSelector = (state, datasheetId, foreign) => {
    const datasheetPack = state.datasheetMap[datasheetId];
    if (!datasheetPack) {
        return null;
    }
    const datasheet = datasheetPack.datasheet;
    if (!datasheet) {
        return null;
    }
    //  Part of the data is approximately equal to no data, can not be calculated, logarithmic table filter will report an error.
    if (!foreign && datasheet.isPartOfData) {
        return null;
    }
    const { snapshot, permissions, name, description } = datasheet;
    const { selection, viewDerivation } = datasheetPack.client;
    return {
        connected: true,
        datasheet: {
            id: datasheetId,
            description,
            datasheetName: name,
            datasheetId,
            permissions,
            snapshot,
            fieldPermissionMap: datasheetPack.fieldPermissionMap,
        },
        client: {
            selection,
            viewDerivation
        }
    };
};
exports.widgetDatasheetSelector = widgetDatasheetSelector;
